import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { renameThread, deleteThread } from "../../../../../api/chat";
import { chatRowClasses } from "./chatUtils";

const THREADS_PER_PAGE = 5;
const EXIT_MS = 180;

const ChatThreadDirectory = ({
  dirKey,
  label,
  Icon,
  color,
  threads,
  currentThreadId,
  isExpanded,
  onToggle,
  onSelect,
  threadRefreshKey,
  onReload,
}) => {
  const [visibleCount, setVisibleCount] = useState(THREADS_PER_PAGE);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [animatedThreads, setAnimatedThreads] = useState(() =>
    (threads || []).map((thread) => ({
      ...thread,
      _isEntering: false,
      _isExiting: false,
    })),
  );

  const renameInputRef = useRef(null);
  const exitTimersRef = useRef({});

  useEffect(() => {
    return () => {
      Object.values(exitTimersRef.current).forEach(clearTimeout);
      exitTimersRef.current = {};
    };
  }, []);

  useEffect(() => {
    setAnimatedThreads((prev) => {
      const nextIds = new Set((threads || []).map((thread) => thread.id));
      const prevIds = new Set(prev.map((thread) => thread.id));

      const updatedPrev = prev
        .map((existing) => {
          const incoming = (threads || []).find(
            (thread) => thread.id === existing.id,
          );

          if (incoming) {
            if (exitTimersRef.current[existing.id]) {
              clearTimeout(exitTimersRef.current[existing.id]);
              delete exitTimersRef.current[existing.id];
            }

            return {
              ...existing,
              ...incoming,
              _isEntering: false,
              _isExiting: false,
            };
          }

          if (!existing._isExiting) {
            exitTimersRef.current[existing.id] = setTimeout(() => {
              setAnimatedThreads((current) =>
                current.filter((thread) => thread.id !== existing.id),
              );

              delete exitTimersRef.current[existing.id];
            }, EXIT_MS);
          }

          return {
            ...existing,
            _isEntering: false,
            _isExiting: true,
          };
        })
        .filter((thread) => nextIds.has(thread.id) || thread._isExiting);

      const additions = (threads || [])
        .filter((thread) => !prevIds.has(thread.id))
        .map((thread) => ({
          ...thread,
          _isEntering: true,
          _isExiting: false,
        }));

      return [...additions, ...updatedPrev].sort((a, b) => {
        const aIndex = (threads || []).findIndex(
          (thread) => thread.id === a.id,
        );
        const bIndex = (threads || []).findIndex(
          (thread) => thread.id === b.id,
        );

        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;

        return aIndex - bIndex;
      });
    });
  }, [threads]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setAnimatedThreads((prev) =>
        prev.map((thread) =>
          thread._isEntering ? { ...thread, _isEntering: false } : thread,
        ),
      );
    });

    return () => cancelAnimationFrame(frame);
  }, [animatedThreads.length]);

  useEffect(() => {
    setVisibleCount(THREADS_PER_PAGE);
  }, [threadRefreshKey]);

  useEffect(() => {
    if (isExpanded) setVisibleCount(THREADS_PER_PAGE);
  }, [isExpanded]);

  useEffect(() => {
    const onRename = (event) => {
      const id = event.detail?.threadId;
      if (!id) return;

      const thread = threads.find((item) => item.id === id);
      if (!thread || thread.isLocal) return;

      setRenamingId(id);
      setRenameValue(thread.label);
      setDeletingId(null);
    };

    const onDelete = (event) => {
      const id = event.detail?.threadId;
      if (!id) return;

      const thread = threads.find((item) => item.id === id);
      if (!thread || thread.isLocal) return;

      setDeletingId(id);
      setRenamingId(null);
    };

    window.addEventListener("chat-thread:start-rename", onRename);
    window.addEventListener("chat-thread:start-delete", onDelete);

    return () => {
      window.removeEventListener("chat-thread:start-rename", onRename);
      window.removeEventListener("chat-thread:start-delete", onDelete);
    };
  }, [threads]);

  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  const commitRename = useCallback(async () => {
    if (!renamingId) return;

    const trimmed = renameValue.trim();

    if (!trimmed) {
      setRenamingId(null);
      return;
    }

    try {
      await renameThread({ thread_id: renamingId, title: trimmed });
      if (onReload) onReload();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-threads"));
    } catch (error) {
      console.error("Rename failed:", error);
    }

    setRenamingId(null);
  }, [renamingId, renameValue, onReload]);

  const cancelRename = useCallback(() => {
    setRenamingId(null);
    setRenameValue("");
  }, []);

  const commitDelete = useCallback(async () => {
    if (!deletingId) return;

    try {
      await deleteThread({ thread_id: deletingId });
      if (onReload) onReload();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-threads"));
    } catch (error) {
      console.error("Delete failed:", error);
    }

    setDeletingId(null);
  }, [deletingId, onReload]);

  const cancelDelete = useCallback(() => {
    setDeletingId(null);
  }, []);

  const activeThread = currentThreadId
    ? animatedThreads.find((thread) => thread.id === currentThreadId)
    : null;

  const visibleThreads = useMemo(
    () => animatedThreads.slice(0, visibleCount),
    [animatedThreads, visibleCount],
  );

  const hasMore = visibleCount < animatedThreads.length;

  const sectionBodyStyle = {
    display: "grid",
    gridTemplateRows: isExpanded ? "1fr" : "0fr",
    opacity: isExpanded ? 1 : 0,
    transition:
      "grid-template-rows 220ms ease, opacity 160ms ease, margin-top 220ms ease",
    marginTop: isExpanded ? 2 : 0,
  };

  const getRowStyle = (thread, index) => ({
    maxHeight: thread._isExiting ? 0 : 34,
    opacity: thread._isExiting || thread._isEntering ? 0 : 1,
    transform:
      thread._isExiting || thread._isEntering
        ? "translateY(-4px) scale(0.985)"
        : "translateY(0) scale(1)",
    transition:
      "max-height 180ms ease, opacity 160ms ease, transform 180ms ease",
    transitionDelay: thread._isEntering
      ? `${Math.min(index * 18, 72)}ms`
      : "0ms",
    overflow: "hidden",
  });

  return (
    <div>
      <button
        onClick={() => onToggle(dirKey)}
        className="group w-full flex items-center gap-x-2 rounded-md px-2 py-1.5 text-[12px] text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition-colors duration-200"
      >
        <Icon
          className="h-3 w-3 shrink-0 transition-transform duration-200 group-hover:scale-110"
          strokeWidth={2}
          style={{ color }}
        />
        <span className="flex-1 text-left font-medium">{label}</span>
        <span
          className="transition-transform duration-200"
          style={{ transform: isExpanded ? "rotate(0deg)" : "rotate(-2deg)" }}
        >
          {isExpanded ? (
            <ChevronUp
              className="h-3 w-3 shrink-0 text-zinc-400"
              strokeWidth={2}
            />
          ) : (
            <ChevronDown
              className="h-3 w-3 shrink-0 text-zinc-400"
              strokeWidth={2}
            />
          )}
        </span>
      </button>

      {!isExpanded && activeThread && (
        <div
          className="ml-2 border-l border-zinc-200/60 dark:border-zinc-700/40 pl-2"
          style={{
            animation: "chat-thread-fade-in 160ms ease both",
          }}
        >
          <button
            onClick={() => onSelect(activeThread.id, dirKey)}
            title={activeThread.label}
            className={chatRowClasses(true)}
          >
            <span className="truncate">{activeThread.label}</span>
          </button>
        </div>
      )}

      <div style={sectionBodyStyle}>
        <div className="overflow-hidden">
          <div className="ml-2 border-l border-zinc-200/60 dark:border-zinc-700/40 pl-2">
            {animatedThreads.length === 0 ? (
              <p className="px-2 py-1 text-[11px] text-zinc-400 dark:text-zinc-600">
                No threads yet
              </p>
            ) : (
              <>
                {visibleThreads.map((thread, index) => (
                  <div
                    key={thread.id}
                    style={getRowStyle(thread, index)}
                    data-context-menu={
                      thread.isLocal ? undefined : "chat-thread"
                    }
                    data-context-payload={
                      thread.isLocal
                        ? undefined
                        : JSON.stringify({
                            thread_id: thread.id,
                            title: thread.label,
                            thread_type: thread.thread_type,
                            isLocal: !!thread.isLocal,
                          })
                    }
                  >
                    {renamingId === thread.id ? (
                      <div className="px-2 py-1">
                        <input
                          ref={renameInputRef}
                          type="text"
                          value={renameValue}
                          onChange={(event) =>
                            setRenameValue(event.target.value)
                          }
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              commitRename();
                            }

                            if (event.key === "Escape") {
                              event.preventDefault();
                              cancelRename();
                            }
                          }}
                          onBlur={commitRename}
                          className="w-full text-[12px] bg-white dark:bg-zinc-800 border border-[#0EA5E9]/40 rounded-md px-2 py-1 text-zinc-700 dark:text-zinc-200 outline-none focus:border-[#0EA5E9] transition-colors"
                        />
                      </div>
                    ) : deletingId === thread.id ? (
                      <div className="flex items-center gap-x-1.5 px-2 py-1.5 text-[11px]">
                        <span className="text-red-500 dark:text-red-400 truncate flex-1">
                          Delete this thread?
                        </span>
                        <button
                          onClick={commitDelete}
                          className="px-2 py-0.5 rounded bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 transition-colors font-medium"
                        >
                          Yes
                        </button>
                        <button
                          onClick={cancelDelete}
                          className="px-2 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => onSelect(thread.id, dirKey)}
                        title={thread.label}
                        className={chatRowClasses(
                          thread.id === currentThreadId,
                        )}
                      >
                        <span className="truncate">{thread.label}</span>
                      </button>
                    )}
                  </div>
                ))}

                {hasMore && (
                  <button
                    onClick={() =>
                      setVisibleCount((prev) => prev + THREADS_PER_PAGE)
                    }
                    className="w-full px-2 py-1.5 text-[11px] text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition-colors text-left"
                  >
                    Load more…
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes chat-thread-fade-in {
          from {
            opacity: 0;
            transform: translateY(-4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default ChatThreadDirectory;
